'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Search, X } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useCallback, useRef, useState } from 'react';

import { BlurredCoverOverlay18Plus } from '@/components/BlurredCoverOverlay18Plus';
import { Input } from '@/components/ui/input';
import { useAgeConfirmationStore } from '@/features/age-verification/store';
import { hasAgeConfirmation } from '@/features/age-verification/utils';
import { useProfile } from '@/hooks/use-profile';
import { cn } from '@/lib/utils';
import { useGlobalSearch } from './hooks/use-global-search';
import { useSearchClickOutside } from './hooks/use-search-click-outside';
import { useSearchDropdownPosition } from './hooks/use-search-dropdown-position';
import type { SearchInputProps, SearchResultItem } from './types';
import { getSearchResultRoute } from './utils/get-search-result-route.util';
import { shouldBlurSearchCover } from './utils/should-blur-search-cover.util';

export const SearchInput = ({
  placeholder = 'Поиск...',
  className = '',
  onSearch,
  variant = 'desktop',
  onSearchStateChange,
}: SearchInputProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { data: session } = useSession();
  const { userData } = useProfile();
  const { checkAndExecute, hasLocalConfirmation } = useAgeConfirmationStore();

  const isAuthenticated = !!session;
  const hasConfirmedAge = hasAgeConfirmation(userData) || hasLocalConfirmation;

  const handleCloseSearch = useCallback(() => {
    setSearchExpanded(false);
    setSearchQuery('');
    setShowDropdown(false);
    onSearchStateChange?.(false);
  }, [onSearchStateChange]);

  useSearchClickOutside({
    containerRef: searchRef,
    onClose: handleCloseSearch,
  });

  useSearchDropdownPosition({
    variant,
    showDropdown,
    searchQuery,
    inputRef,
    setDropdownPosition,
  });

  useGlobalSearch({
    searchQuery,
    setSearchResults,
    setIsSearching,
    setShowDropdown,
  });

  const navigateToResult = (item: SearchResultItem) => {
    if (variant === 'mobile') {
      handleCloseSearch();
    } else {
      setShowDropdown(false);
      setSearchQuery('');
    }

    router.push(getSearchResultRoute(item));
  };

  const handleResultClick = (item: SearchResultItem) => {
    const isRating18 = item.type === 'fanfic' && item.rating === '18';

    if (isRating18 && !hasConfirmedAge) {
      // Close search dropdown so age dialog (z-50) is not covered by z-[99999]
      setShowDropdown(false);
      if (variant === 'mobile') {
        setSearchExpanded(false);
        onSearchStateChange?.(false);
      }

      checkAndExecute(
        () => {
          router.push(getSearchResultRoute(item));
        },
        true,
        isAuthenticated,
        hasConfirmedAge,
      );
      return;
    }

    navigateToResult(item);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (onSearch) {
      onSearch(searchQuery);
    }
    setShowDropdown(false);
  };

  const renderSearchIcon = () => {
    if (isSearching) {
      return <Loader2 className="w-4 h-4 text-[#898989] animate-spin" />;
    }
    return <Search className="w-[13px] h-[13px] max-md:w-4 max-md:h-4 text-[#898989]" />;
  };

  const renderDropdown = () => {
    const isFixed = variant === 'desktop';
    const positionStyle = isFixed
      ? {
          position: 'fixed' as const,
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
        }
      : {};

    return (
      <AnimatePresence>
        {showDropdown && (
          <motion.div
            key="dropdown"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={
              isFixed
                ? 'bg-[#25282E] border border-[#3E3E3E] rounded-md shadow-lg z-[99999] max-h-80 overflow-y-auto scrollbar-search-dropdown'
                : 'absolute top-full left-0 mt-2 w-full bg-[#25282E] border border-[#3E3E3E] rounded-md shadow-lg z-[99999] max-h-80 overflow-y-auto scrollbar-search-dropdown'
            }
            style={positionStyle}
          >
            {isSearching ? (
              <div className="p-4 text-center text-[#898989]">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Поиск...
              </div>
            ) : searchResults.length > 0 ? (
              <div className="p-2">
                {searchResults.map((item) => {
                  const shouldBlurCover = shouldBlurSearchCover(item, hasConfirmedAge);
                  const coverUrl = item.heroImage?.url || item.heroImage?.thumbnailURL;

                  return (
                    <div
                      key={`${item.type}-${item.id}`}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleResultClick(item);
                      }}
                      className="w-full flex items-center space-x-3 p-3 hover:bg-[#3a3f47] rounded transition-colors text-left cursor-pointer"
                    >
                      <div className="relative w-12 h-12 bg-[#3a3f47] rounded overflow-hidden flex-shrink-0">
                        {coverUrl ? (
                          <Image
                            src={coverUrl}
                            alt={item.title}
                            width={48}
                            height={48}
                            className={cn('h-full w-full object-cover', shouldBlurCover && 'blur')}
                            sizes="48px"
                          />
                        ) : (
                          <div className="w-full h-full bg-[#4a4f57] flex items-center justify-center">
                            <span className="text-[#898989] text-xs">IMG</span>
                          </div>
                        )}
                        {shouldBlurCover && (
                          <BlurredCoverOverlay18Plus
                            className="rounded"
                            labelClassName="text-[10px] leading-none"
                          />
                        )}
                      </div>
                      <span className="text-white text-sm truncate">{item.title}</span>
                    </div>
                  );
                })}
              </div>
            ) : searchQuery.trim().length >= 2 && !isSearching ? (
              <div className="p-4 text-center text-[#898989]">
                По вашему запросу ничего не найдено
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  if (variant === 'mobile') {
    return (
      <div
        className="relative"
        ref={searchRef}
        style={{ position: 'relative', zIndex: 1000 }}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <AnimatePresence mode="wait">
          {!searchExpanded ? (
            <motion.button
              key="search-button"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              onClick={() => {
                setSearchExpanded(true);
                onSearchStateChange?.(true);
              }}
              className="w-10 h-10 rounded-lg bg-[#25282E] flex items-center justify-center"
            >
              {renderSearchIcon()}
            </motion.button>
          ) : (
            <motion.div
              key="search-input"
              initial={{ opacity: 0, width: 40 }}
              animate={{ opacity: 1, width: '100%' }}
              exit={{ opacity: 0, width: 40 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="relative w-full"
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                {renderSearchIcon()}
              </div>
              <Input
                ref={inputRef}
                placeholder={placeholder}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="pl-10 pr-10 bg-[#25282E] border-none text-white placeholder:text-[#898989] rounded-[10px] focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-11 w-full text-[16px] leading-5 sm:text-sm sm:leading-5"
                autoFocus
              />
              <button
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleCloseSearch();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 z-20"
              >
                <X className="w-4 h-4 text-[#898989]" />
              </button>

              {renderDropdown()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="relative" ref={searchRef} style={{ position: 'relative', zIndex: 1000 }}>
      <form onSubmit={handleSearchSubmit} className="relative">
        <Input
          ref={inputRef}
          placeholder={placeholder}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className={`bg-[#25282E] border-none text-white placeholder:text-[#898989] rounded-lg focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 ${className}`}
          autoFocus
        />
      </form>

      {renderDropdown()}
    </div>
  );
};
